import { QueryClient } from "@tanstack/react-query";

import { config } from "@/config";
import { attemptRefresh } from "@/auth/flow";
import { getSnapshot } from "@/auth/state";

/**
 * Tanstack Query client factory.
 *
 * Defaults:
 * - 5 minute stale time: low-volume clinical-author surface, server is
 *   stateless (ADR-0040), aggressive caching is safe.
 * - 1 retry on failure: surface real errors quickly; the auth interceptor
 *   handles 401-driven refresh separately (Phase 2).
 * - No refetch on window focus: clinical authors switch tabs constantly;
 *   focus-driven refetch creates UX noise without value.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: 0,
      },
    },
  });
}

/**
 * Authenticated fetch wrapper. Attaches `Authorization: Bearer <token>` to
 * every request, attempts a single silent refresh on 401, and redirects to
 * `/login?next=<path>` if the refresh fails.
 *
 * Usage from a Tanstack Query `queryFn`:
 *
 *   useQuery({
 *     queryKey: ["catalogs"],
 *     queryFn: () => apiFetch<CatalogList>("/api/v1/catalogs"),
 *   });
 */
export async function apiFetch<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const url = path.startsWith("http") ? path : `${config.apiBaseUrl}${path}`;
  const resp = await fetchWithAuth(url, init);
  if (resp.status === 401) {
    const refreshed = await attemptRefresh();
    if (!refreshed) {
      // Redirect to /login preserving the path the user was on. Module-level
      // `window.location.assign` keeps this independent of React Router so
      // the interceptor works from non-component contexts (e.g. mutations).
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.assign(`/login?next=${next}`);
      throw new Error("Authentication required.");
    }
    const retried = await fetchWithAuth(url, init);
    if (!retried.ok) {
      throw await apiError(retried);
    }
    return (await retried.json()) as T;
  }
  if (!resp.ok) {
    throw await apiError(resp);
  }
  return (await resp.json()) as T;
}

/**
 * Turn a failed response into an Error carrying the SERVER'S OWN reason.
 *
 * The BFF error envelope is `{error: {code, message, detail, …}, status}`, and
 * `detail` is the actionable half: the BFF's fixed reason per IAM status (IAM's own
 * text never reaches it, docs#171), or "user invite is not enabled". Without surfacing it,
 * every failure collapses to "API request failed: <status>", which throws away
 * the distinct error codes the backend takes care to return (a 409
 * PORTAL_USER_CONFLICT reads identically to a 503, so "pick another username" and
 * "the service is down" become the same sentence). Falls back to the status line
 * for a non-JSON or unenveloped body.
 */
async function apiError(resp: Response): Promise<Error> {
  const base = `API request failed: ${resp.status} ${resp.statusText}`;
  try {
    const body = (await resp.json()) as {
      error?: { detail?: unknown; message?: unknown };
      detail?: unknown;
      message?: unknown;
    };
    const reason =
      asText(body.error?.detail) ??
      asText(body.error?.message) ??
      asText(body.detail) ??
      asText(body.message);
    return new Error(reason ? `${base} — ${reason}` : base);
  } catch {
    // Body already consumed, or not JSON — the status line is all we have.
    return new Error(base);
  }
}

function asText(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() !== "" ? value : undefined;
}

/**
 * Low-level fetch with the bearer-token header attached. Exposed as a
 * separate function so tests can exercise the header injection without
 * the full retry+redirect stack.
 */
export async function fetchWithAuth(url: string, init: RequestInit = {}): Promise<Response> {
  const tokens = getSnapshot().tokens;
  const headers = new Headers(init.headers);
  if (tokens !== null) {
    headers.set("Authorization", `Bearer ${tokens.accessToken}`);
  }
  // Default JSON bodies to application/json — but NOT FormData, whose multipart
  // Content-Type (with its boundary) the browser must set itself.
  if (!headers.has("Content-Type") && init.body !== undefined && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  return fetch(url, { ...init, headers });
}
